/**
 * RecentAuditLogs widget — stream of recent mutation events across the
 * tenant.
 *
 * Migrated to live API in Wave 3.6. Reads GET
 * /v1/system/audit-logs/activity with an optional filter by action
 * type. The mock's four 'type' buckets (error / warning / success /
 * info) don't exist on the backend AuditLog schema — the real fields
 * are \`action\` (e.g. 'create', 'update', 'delete') and \`module\` (e.g.
 * 'organization', 'attendance'). We map action → severity client-side
 * so the same chip / icon vocabulary lands:
 *
 *   action ∈ {'delete', 'deactivate'}   → error (red)
 *   action ∈ {'update', 'patch'}        → warning (amber)
 *   action ∈ {'create', 'invite'}       → success (green)
 *   action == anything else             → info (blue)
 *
 * The filter dropdown now filters by \`module\` (actual backend field)
 * instead of the mock's severity — operators filter by "where this
 * happened" more naturally than by "what severity", and it matches
 * how the backend indexes audit rows.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha, Box, Chip, CircularProgress, FormControl, List, ListItem,
  MenuItem, Paper, Select, Typography, useTheme,
} from '@mui/material';
import {
  AddCircleOutline, CheckCircle as SuccessIcon, DeleteOutline,
  Edit, Error as ErrorIcon, FilterList as FilterIcon, Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, systemApi } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import type { AuditLog } from '../../../../api/system';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../../i18n/translateApiError';
import {
  formatRelativeTime, translateAuditAction, translateAuditModule, translateAuditResource,
  type WidgetTranslator,
} from './widgetDisplay';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';
import { FactCheckOutlined } from '@mui/icons-material';

type Severity = 'error' | 'warning' | 'success' | 'info';

function mapAction(action: string | null | undefined): Severity {
  if (!action) return 'info';
  if (action === 'delete' || action === 'deactivate' || action === 'terminate') return 'error';
  if (action === 'update' || action === 'patch') return 'warning';
  if (action === 'create' || action === 'invite') return 'success';
  return 'info';
}

function formatMessage(log: AuditLog, t: WidgetTranslator): string {
  return t('dashboard:widgets.recentAuditLogs.message', {
    action: translateAuditAction(t, log.action),
    resource: translateAuditResource(t, log.resource_type),
    id: log.resource_id ? ` (${log.resource_id.slice(0, 8)})` : '',
  });
}

function actionIconFor(severity: Severity): React.ReactNode {
  switch (severity) {
    case 'error': return <DeleteOutline />;
    case 'warning': return <Edit />;
    case 'success': return <AddCircleOutline />;
    default: return <InfoIcon />;
  }
}

const RecentAuditLogs: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await systemApi.auditApi.listActivity({ limit: 25 });
        if (!cancelled) setLogs(rows);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.recentAuditLogs.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const moduleOptions = useMemo(() => {
    const set = new Set<string>();
    for (const l of logs) if (l.module) set.add(l.module);
    return ['all', ...Array.from(set).sort()];
  }, [logs]);

  const filtered = useMemo(() => {
    const base = moduleFilter === 'all' ? logs : logs.filter((l) => l.module === moduleFilter);
    return base.slice(0, 15); // widget frame can't fit more comfortably
  }, [logs, moduleFilter]);

  const getIcon = (severity: Severity): React.ReactNode => {
    switch (severity) {
      case 'error': return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      case 'warning': return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case 'success': return <SuccessIcon sx={{ color: theme.palette.success.main }} />;
      default: return <InfoIcon sx={{ color: theme.palette.info.main }} />;
    }
  };

  const chipColor = (severity: Severity) => {
    switch (severity) {
      case 'error': return { bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main };
      case 'warning': return { bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main };
      case 'success': return { bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main };
      default: return { bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main };
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <WidgetErrorState error={error} />;
  }

  if (logs.length === 0) {
    return (
      <WidgetEmptyState icon={FactCheckOutlined} title={t('dashboard:widgets.recentAuditLogs.noAudit')} />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Filter row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}
              variant="outlined"
              sx={{ '& .MuiSelect-select': { py: 1, fontSize: '0.875rem' } }}>
              {moduleOptions.map((m) => (
                <MenuItem key={m} value={m}>
                  {m === 'all' ? t('dashboard:widgets.recentAuditLogs.allModules') : translateAuditModule(t, m)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <List sx={{ flex: 1, overflow: 'auto' }}>
        {filtered.map((log) => {
          const severity = mapAction(log.action);
          return (
            <ListItem
              key={log.id}
              component={Paper}
              elevation={0}
              onClick={() => navigate('/system/audit-logs/activity')}
              sx={{ py: 1, mb: 0.5, borderRadius: 1, bgcolor: 'background.paper',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                cursor: 'pointer',
                border: '1px solid transparent',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Box sx={{ mr: 1.5 }}>{getIcon(severity) || actionIconFor(severity)}</Box>
                  <Typography variant="body2"
                    sx={{ fontWeight: 500, flex: 1, color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' } }}>
                    {formatMessage(log, t)}
                  </Typography>
                </Box>
                <Typography variant="caption"
                  sx={{ color: 'text.secondary', ml: 2, whiteSpace: 'nowrap' }}>
                  {formatRelativeTime(log.created_at, t, i18n.language)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 4.5 }}>
                {log.module && (
                  <Chip label={translateAuditModule(t, log.module)} size="small"
                    sx={{ height: 20, fontSize: '0.75rem', ...chipColor(severity) }} />
                )}
                {log.worker_id && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {t('dashboard:widgets.recentAuditLogs.byWorker', { id: log.worker_id.slice(0, 8) })}
                  </Typography>
                )}
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default RecentAuditLogs;
